import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Locale } from "../api/types";
import en, { type TranslationDict } from "./en.js";
import zh_HK from "./zh_HK.js";
import zh_CN from "./zh_CN.js";

const DICTS: Record<Locale, TranslationDict> = {
  en,
  zh_HK,
  zh_CN,
};

const STORAGE_KEY = "helix.locale";

/** Browser-detected default locale, falling back to en. */
export function detectLocale(): Locale {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && stored in DICTS) return stored as Locale;
  const nav = navigator.language || "en";
  if (nav.startsWith("zh-HK") || nav.startsWith("zh-TW") || nav.startsWith("zh-Hant")) return "zh_HK";
  if (nav.startsWith("zh") || nav.startsWith("zh-CN") || nav.startsWith("zh-Hans")) return "zh_CN";
  return "en";
}

type TFn = (key: keyof TranslationDict, vars?: Record<string, string | number>) => string;

interface I18nState {
  locale: Locale;
  localeVersion: number;
  setLocale: (locale: Locale) => void;
  t: TFn;
}

const I18nContext = createContext<I18nState | undefined>(undefined);

export function I18nProvider({
  children,
  initialLocale,
}: {
  children: ReactNode;
  initialLocale?: Locale;
}) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale ?? detectLocale());
  const [version, setVersion] = useState(0);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    setVersion((v) => v + 1);
    localStorage.setItem(STORAGE_KEY, l);
  }, []);

  // Keep <html lang> in sync for accessibility.
  useEffect(() => {
    document.documentElement.lang = locale === "en" ? "en" : "zh-Hant";
  }, [locale]);

  const t = useCallback<TFn>(
    (key, vars) => {
      const dict = DICTS[locale] ?? en;
      let str = (dict[key] ?? en[key] ?? key) as string;
      if (vars) {
        for (const [k, v] of Object.entries(vars)) {
          str = str.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
        }
      }
      return str;
    },
    [locale],
  );

  const value = useMemo<I18nState>(() => ({ locale, localeVersion: version, setLocale, t }), [locale, version, setLocale, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useT(): I18nState {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useT must be used within I18nProvider");
  return ctx;
}

export const LOCALES: Locale[] = ["en", "zh_HK", "zh_CN"];
